import {LookToken, Look, TargetMode} from './requesters/Look';
import {SayToken, Say} from './requesters/Say';
import {AttentionToken, Attention} from './requesters/Attention';
import {CommandRequester} from '../../../CommandRequester';

/**
 * Commands for working with Jibo's verbal and physical modes of expression
 * @namespace CommandRequester.expression
 */
export class Expression {

    attentionRequest:Attention;
    lookRequest:Look;
    sayRequest:Say;

    constructor(requester:CommandRequester) {
        this.attentionRequest = new Attention(requester);
        this.lookRequest = new Look(requester);
        this.sayRequest = new Say(requester);
    }

    /**
     * Make Jibo turn to look at the specified target
     * @method CommandRequester.expression#look
     * @param  {CommandRequester.expression.LookAtTarget} target Target to look at
     * @param  {boolean} [shouldTrack] Currently unsupported
     * @param  {boolean} [levelHead=true] `true` to keep Jibo's head level while he moves
     * @return {LookToken}
     */
    look(
        lookAtTarget: CommandRequester.expression.LookAtTarget,
        shouldTrack?: boolean,
        levelHead: boolean = true,
    ): LookToken {
        let protocol;

        switch(lookAtTarget.type) {
            case "ANGLE":
                const angle: [number, number] = [lookAtTarget.angle.theta, lookAtTarget.angle.psi];
                protocol = Look.generateProtocol(TargetMode.Angle, angle, levelHead, false);
                break;
            case "POSITION":
                const position: [number, number, number] = [lookAtTarget.position.x, lookAtTarget.position.y, lookAtTarget.position.z];
                protocol = Look.generateProtocol(TargetMode.Position, position, levelHead, false);
                break;
            case "ENTITY":
                const target: number = lookAtTarget.target;
                protocol = Look.generateProtocol(TargetMode.Entity, target, levelHead, false);
                break;
            case "CAMERA":
                const coords: [number, number] = [lookAtTarget.coords.x, lookAtTarget.coords.y];
                protocol = Look.generateProtocol(TargetMode.ScreenCoords, coords, levelHead, shouldTrack);
                break;
        }

        return this.lookRequest.generateToken(protocol, true) as LookToken;
    }

    /**
     * Make Jibo speak.
     * @method CommandRequester.expression#say
     * @param {string} text Plain text or Embodied Speech Markup Language to say. See {@link https://app-toolkit.jibo.com/esml/}.
     * @return {SayToken}
     */
    public say(
        text: string,
        speakOptions?: JIBO.v1.SpeakOptions,
        autoRuleConfig?: JIBO.v1.AutoRuleConfig,
    ): SayToken {
        const protocol = Say.generateProtocol(text, speakOptions, autoRuleConfig);
        return this.sayRequest.generateToken(protocol, true);
    }

    /**
     * Set Jibo's attention mode.
     * @method CommandRequester.expression#setAttention
     * @param {CommandRequester.expression.AttentionMode} mode Attention mode to which to set the robot
     * @return {AttentionToken}
     */
    public setAttention(
        mode: CommandRequester.expression.AttentionMode,
    ): AttentionToken {
        const protocol = Attention.generateProtocol(mode);
        return this.attentionRequest.generateToken(protocol, true);
    }
}